<template>
	<HorizontalSplit>
		<template #top>
			<div class="map-wrap">
				<mgl-map
					:map-style="mapStyleURL(ALIDADE_SMOOTH)"
					:center="initialCenter"
					:zoom="11"
					@map:load="onMapLoad"
				>
					<mgl-geo-json-source
						v-if="treatment?.geometry"
						source-id="treatment-area"
						:data="treatment.geometry"
					>
						<mgl-fill-layer
							layer-id="treatment-area-fill"
							:paint="{ 'fill-color': '#22c55e', 'fill-opacity': 0.3 }"
						/>
						<mgl-line-layer
							layer-id="treatment-area-outline"
							:paint="{ 'line-color': '#22c55e', 'line-width': 2 }"
						/>
						<mgl-circle-layer
							layer-id="treatment-area-points"
							:filter="pointOnlyFilter"
							:paint="{
								'circle-color': '#22c55e',
								'circle-opacity': 0.6,
								'circle-radius': 8,
								'circle-stroke-color': '#22c55e',
								'circle-stroke-width': 2,
							}"
						/>
					</mgl-geo-json-source>
				</mgl-map>
				<div v-if="treatment && !treatment.geometry" class="map-note">
					No treatment area recorded.
				</div>
			</div>
		</template>

		<template #bottom>
			<div class="container-fluid py-4">
				<div class="row mb-3">
					<div class="col">
						<RouterLink
							to="/_/review/treatment"
							class="btn btn-outline-secondary btn-sm"
						>
							<i class="bi bi-arrow-left"></i> All treatments
						</RouterLink>
					</div>
				</div>

				<div class="row mb-3">
					<div class="col">
						<h1 class="h2">Treatment #{{ treatment ? treatment.id : id }}</h1>
					</div>
				</div>

				<div v-if="loading" class="text-center py-5">
					<div class="spinner-border text-primary" role="status"></div>
					<p class="mt-2 text-muted">Loading treatment…</p>
				</div>

				<div v-else-if="!treatment" class="text-center py-4 text-muted">
					Treatment not found.
				</div>

				<div v-else class="card">
					<div class="card-header">Treatment Info</div>
					<div class="card-body">
						<dl class="row mb-0">
							<dt class="col-sm-4 text-muted">Treatment ID</dt>
							<dd class="col-sm-8">
								<code>{{ treatment.id }}</code>
							</dd>

							<dt class="col-sm-4 text-muted">Organization ID</dt>
							<dd class="col-sm-8">
								<code>{{ treatment.organization_id }}</code>
							</dd>

							<dt class="col-sm-4 text-muted">Creator</dt>
							<dd class="col-sm-8">{{ creatorText }}</dd>

							<dt class="col-sm-4 text-muted">Product</dt>
							<dd class="col-sm-8">{{ productText }}</dd>

							<dt class="col-sm-4 text-muted">Method</dt>
							<dd class="col-sm-8">{{ treatment.method || "—" }}</dd>

							<dt class="col-sm-4 text-muted">Area Treated</dt>
							<dd class="col-sm-8">{{ areaText }}</dd>

							<dt class="col-sm-4 text-muted">Quantity</dt>
							<dd class="col-sm-8">{{ quantityText }}</dd>

							<dt class="col-sm-4 text-muted">Start Time</dt>
							<dd class="col-sm-8">
								<LocalizedTime :datetime="treatment.start_datetime" />
							</dd>

							<dt class="col-sm-4 text-muted">End Time</dt>
							<dd class="col-sm-8">
								<LocalizedTime
									v-if="treatment.end_datetime"
									:datetime="treatment.end_datetime"
								/>
								<span v-else>—</span>
							</dd>

							<dt class="col-sm-4 text-muted">Duration</dt>
							<dd class="col-sm-8">
								{{
									formatDuration(
										durationSeconds(
											treatment.start_datetime,
											treatment.end_datetime,
										),
									)
								}}
							</dd>

							<dt class="col-sm-4 text-muted">Created</dt>
							<dd class="col-sm-8">
								<LocalizedTime :datetime="treatment.created" />
							</dd>

							<dt class="col-sm-4 text-muted">Geometry</dt>
							<dd class="col-sm-8">
								{{ treatment.geometry ? "Recorded" : "None" }}
							</dd>
						</dl>
					</div>
				</div>

				<!-- VectorSurv filing capture. A treatment files as a VectorSurv
				     Application only when its equipment and applicator are captured
				     against it, and a treatment is a terminal record: this appends an
				     annotation, so a correction is a new row and both are kept. The
				     applicator defaults to the treatment's creator, which is what a
				     field source already means. -->
				<div v-if="treatment" class="card mt-3 filing-card">
					<div
						class="card-header d-flex justify-content-between align-items-center"
					>
						<span>VectorSurv filing</span>
						<span v-if="filingSaved" class="badge text-bg-success">saved</span>
					</div>
					<div class="card-body">
						<p class="text-muted small mb-3">
							What VectorSurv filing will send for this treatment. The capture
							is appended, so saving again records a correction and keeps the
							earlier one.
						</p>

						<dl v-if="annotation" class="row mb-3">
							<dt class="col-sm-3 text-muted">Equipment</dt>
							<dd class="col-sm-9">
								{{
									annotation.equipmentName ||
									(annotation.equipmentId !== null
										? `#${annotation.equipmentId}`
										: "—")
								}}
							</dd>

							<dt class="col-sm-3 text-muted">Applicator</dt>
							<dd class="col-sm-9">
								<template v-if="annotation.applicatorName">
									{{ annotation.applicatorName }}
									<span
										v-if="annotation.applicatorIsCreator"
										class="badge text-bg-light ms-1"
									>
										the treatment's creator
									</span>
								</template>
								<span v-else class="text-muted">—</span>
							</dd>

							<dt class="col-sm-3 text-muted">Recorded</dt>
							<dd class="col-sm-9">
								<LocalizedTime
									v-if="annotation.recordedAt"
									:datetime="annotation.recordedAt"
								/>
								<span v-else class="text-muted">Never captured</span>
							</dd>
						</dl>

						<div class="row g-2 align-items-end">
							<div class="col-md-5">
								<label class="form-label" for="filing-equipment">
									Equipment
								</label>
								<select
									id="filing-equipment"
									v-model.number="filingEquipmentID"
									class="form-select"
									:disabled="filingSaving || equipmentLoading"
								>
									<option :value="null">— choose —</option>
									<option
										v-for="rig in activeEquipment"
										:key="rig.id"
										:value="rig.id"
									>
										{{ rig.name }}
									</option>
								</select>
							</div>
							<div class="col-md-4">
								<label class="form-label" for="filing-applicator">
									Applicator
								</label>
								<select
									id="filing-applicator"
									v-model.number="filingApplicatorID"
									class="form-select"
									:disabled="filingSaving || usersLoading"
								>
									<option :value="null">
										The treatment's creator{{
											treatment.creator_name
												? ` (${treatment.creator_name})`
												: ""
										}}
									</option>
									<option
										v-for="member in users"
										:key="member.id"
										:value="member.id"
									>
										{{ member.displayName || member.username }}
									</option>
								</select>
							</div>
							<div class="col-md-3">
								<ButtonLoading
									text="Save capture"
									icon="bi bi-clipboard-check"
									:loading="filingSaving"
									:disabled="filingEquipmentID === null"
									@click="saveFilingCapture"
								/>
							</div>
						</div>

						<div
							v-if="filingError"
							class="alert alert-danger mt-3 mb-0"
							role="alert"
						>
							{{ filingError }}
						</div>
					</div>
				</div>

				<div v-if="treatment" class="row mt-3">
					<div class="col-lg-6 mb-3">
						<div class="card h-100">
							<div
								class="card-header d-flex justify-content-between align-items-center"
							>
								<span>Feature</span>
								<RouterLink
									:to="{
										name: 'Feature Review',
										params: { id: treatment.feature_id.toString() },
									}"
									class="btn btn-sm btn-outline-primary"
								>
									<i class="bi bi-box-arrow-up-right"></i> View feature
								</RouterLink>
							</div>
							<div class="card-body">
								<dl v-if="feature" class="row mb-0">
									<dt class="col-sm-4 text-muted">Feature ID</dt>
									<dd class="col-sm-8">
										<code>{{ feature.id }}</code>
									</dd>

									<dt class="col-sm-4 text-muted">Type</dt>
									<dd class="col-sm-8">
										<span class="badge bg-primary">{{ feature.type }}</span>
									</dd>

									<dt class="col-sm-4 text-muted">Condition</dt>
									<dd class="col-sm-8">
										<span v-if="feature.pool" class="badge bg-secondary">
											{{ feature.pool.current_condition }}
										</span>
										<span v-else class="text-muted">No pool</span>
									</dd>

									<dt class="col-sm-4 text-muted">Location</dt>
									<dd class="col-sm-8">{{ featureLocationText }}</dd>
								</dl>

								<div v-else-if="featureFailed" class="text-muted">
									Could not load feature #{{ treatment.feature_id }}.
								</div>

								<div v-else class="text-muted">
									<span
										class="spinner-border spinner-border-sm me-2"
										role="status"
									></span>
									Loading feature…
								</div>
							</div>
						</div>
					</div>

					<div class="col-lg-6 mb-3">
						<div class="card h-100">
							<div class="card-header">Site</div>
							<div class="card-body">
								<template v-if="site">
									<dl class="row mb-0">
										<dt class="col-sm-4 text-muted">Site ID</dt>
										<dd class="col-sm-8">
											<code>{{ site.id }}</code>
										</dd>

										<dt class="col-sm-4 text-muted">Address</dt>
										<dd class="col-sm-8">{{ siteAddressText }}</dd>

										<dt class="col-sm-4 text-muted">Tags</dt>
										<dd class="col-sm-8">
											<template v-if="siteTags.length">
												<span
													v-for="[key, value] in siteTags"
													:key="key"
													class="badge bg-light text-dark me-1"
												>
													{{ value ? `${key}: ${value}` : key }}
												</span>
											</template>
											<span v-else class="text-muted">—</span>
										</dd>

										<dt class="col-sm-4 text-muted">Notes</dt>
										<dd class="col-sm-8">
											<span v-if="site.notes" class="notes-clamp">
												{{ site.notes }}
											</span>
											<span v-else class="text-muted">—</span>
										</dd>
									</dl>

									<div class="mt-3">
										<RouterLink
											:to="routes.SiteDetail(site.id)"
											class="btn btn-sm btn-outline-primary"
										>
											<i class="bi bi-box-arrow-up-right"></i>
											View site details
										</RouterLink>
									</div>
								</template>

								<div v-else-if="siteMessage" class="text-muted">
									{{ siteMessage }}
								</div>

								<div v-else class="text-muted">
									<span
										class="spinner-border spinner-border-sm me-2"
										role="status"
									></span>
									Loading site…
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</HorizontalSplit>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<style scoped>
.map-wrap {
	position: relative;
	width: 100%;
	height: 100%;
	overflow: hidden;
}

.map-note {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(255, 255, 255, 0.9);
	border-radius: 6px;
	padding: 8px 14px;
	color: #6c757d;
	font-size: 0.875rem;
	pointer-events: none;
}

/* Long free-text notes must not stretch the site card past the row height. */
.notes-clamp {
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 4;
	line-clamp: 4;
	overflow: hidden;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
	MglCircleLayer,
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	MglMap,
	type MglEvent,
	useMap,
} from "vue-maplibre-gl";

import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import LocalizedTime from "@/components/LocalizedTime.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { apiClient } from "@/client";
import { durationSeconds, formatAddress, formatDuration } from "@/format";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import { useRoutes } from "@/route/use";
import { useFeatureStore } from "@/store/feature";
import { useSessionStore } from "@/store/session";
import { useStoreSite } from "@/store/site";
import { useStoreTreatment } from "@/store/treatment";
import type { FeatureDetail, Site, Treatment } from "@/type/api";
import type {
	VectorSurvEquipment,
	VectorSurvFilingAnnotation,
	VectorSurvFilingAnnotationResult,
	VectorSurvUser,
} from "@/type/vectorsurv";

interface Props {
	id: string;
}

// Zoom used when the recorded geometry is a point rather than an area.
const POINT_ZOOM = 16;

// A circle layer also drops a dot on every polygon vertex, which reads as
// editing handles on an area treatment. vue-maplibre-gl's filter prop type
// cannot express the geometry-type expression, hence the cast.
const pointOnlyFilter = ["==", ["geometry-type"], "Point"] as any;

const props = defineProps<Props>();
const store = useStoreTreatment();
const featureStore = useFeatureStore();
const siteStore = useStoreSite();
const session = useSessionStore();
const routes = useRoutes();
const mapInstance = useMap();

const treatment = ref<Treatment | null>(null);
const feature = ref<FeatureDetail | null>(null);
const featureFailed = ref(false);
const site = ref<Site | null>(null);
const siteMessage = ref("");
const loading = ref(true);
const mapReady = ref(false);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

onMounted(async () => {
	try {
		treatment.value = await store.fetchByID(parseInt(props.id, 10));
	} catch (err) {
		toastTitle.value = "Failed to load treatment";
		toastMessage.value =
			err instanceof Error ? err.message : "Failed to load treatment.";
		toastShow.value = true;
	} finally {
		loading.value = false;
	}

	if (treatment.value) {
		void loadFilingCapture();
		await loadFeatureAndSite(treatment.value.feature_id);
	}
});

// The filing panel: the current annotation, the district's live equipment, and
// the organization's users (the applicator's override list). The three reads are
// independent, so they run together; each failure degrades its own control
// rather than blanking the page.
const annotation = ref<VectorSurvFilingAnnotation | null>(null);
const equipment = ref<VectorSurvEquipment[]>([]);
const users = ref<VectorSurvUser[]>([]);
const equipmentLoading = ref(false);
const usersLoading = ref(false);
const filingSaving = ref(false);
const filingError = ref("");
const filingSaved = ref(false);
const filingEquipmentID = ref<number | null>(null);
const filingApplicatorID = ref<number | null>(null);

const activeEquipment = computed(() =>
	equipment.value.filter((rig) => rig.active),
);

async function loadFilingCapture(): Promise<void> {
	equipmentLoading.value = true;
	usersLoading.value = true;
	try {
		const [current, rigs, members] = await Promise.all([
			apiClient.JSONGet<VectorSurvFilingAnnotation>(
				`/api/integration/vectorsurv/filing/annotation?treatmentId=${props.id}`,
			),
			apiClient.JSONGet<VectorSurvEquipment[]>(
				"/api/integration/vectorsurv/equipment",
			),
			apiClient.JSONGet<VectorSurvUser[]>("/api/integration/vectorsurv/users"),
		]);
		annotation.value = current;
		equipment.value = rigs;
		users.value = members;
		filingEquipmentID.value = current.equipmentId;
		filingApplicatorID.value = current.applicatorIsCreator
			? null
			: current.applicatorId;
	} catch (err) {
		filingError.value =
			err instanceof Error
				? err.message
				: "Could not load the VectorSurv filing capture.";
	} finally {
		equipmentLoading.value = false;
		usersLoading.value = false;
	}
}

// Saving appends an annotation row; the panel then re-reads the current one, so
// what it shows is the server's answer rather than the form's intent.
async function saveFilingCapture(): Promise<void> {
	if (filingSaving.value || filingEquipmentID.value === null) return;
	filingSaving.value = true;
	filingError.value = "";
	filingSaved.value = false;
	try {
		await apiClient.JSONPost<VectorSurvFilingAnnotationResult>(
			"/api/integration/vectorsurv/filing/annotation",
			{
				treatmentId: parseInt(props.id, 10),
				equipmentId: filingEquipmentID.value,
				applicatorId: filingApplicatorID.value,
			},
		);
		await loadFilingCapture();
		filingSaved.value = true;
	} catch (err) {
		filingError.value =
			err instanceof Error
				? err.message
				: "Could not record the VectorSurv filing capture.";
	} finally {
		filingSaving.value = false;
	}
}

// The feature and its site are secondary context for the treatment. A failed
// lookup degrades the affected card to a muted note instead of blocking the
// page or throwing away the treatment that did load.
async function loadFeatureAndSite(featureId: number): Promise<void> {
	let featureDetail: FeatureDetail;
	try {
		featureDetail = await featureStore.fetchByID(featureId);
	} catch {
		featureFailed.value = true;
		siteMessage.value = "Site unknown — the feature could not be loaded.";
		return;
	}
	feature.value = featureDetail;

	if (!featureDetail.site_id) {
		siteMessage.value = "This feature is not linked to a site.";
		return;
	}

	try {
		site.value = await siteStore.fetchOne(featureDetail.site_id);
	} catch {
		siteMessage.value = "Could not load site details.";
	}
}

// Start centered on the district's service area when available.
const initialCenter = computed<[number, number]>(() => {
	const sa = session.organization?.service_area;
	if (sa && sa.min) {
		return [
			(sa.min.longitude + sa.max.longitude) / 2,
			(sa.min.latitude + sa.max.latitude) / 2,
		];
	}
	return [-119.175, 36.24];
});

// A point is not a usable bounds: fitBounds on a zero-size box zooms to the
// maximum, so single points are flown to directly. MultiPoint and area
// geometries have a real box and are fitted.
function focusTreatmentArea(): void {
	const map = mapInstance.map;
	const geometry = treatment.value?.geometry;
	if (!mapReady.value || !map || !geometry) return;

	const bounds = boundsFromGeoJSON(geometry, 20);
	if (!bounds) return;

	const sw = bounds.getSouthWest();
	const ne = bounds.getNorthEast();
	if (sw.lng === ne.lng && sw.lat === ne.lat) {
		map.flyTo({ center: [sw.lng, sw.lat], zoom: POINT_ZOOM, duration: 800 });
		return;
	}
	map.fitBounds(bounds, { padding: 40, duration: 800 });
}

// The map's load event and the treatment's fetch race each other, so centering
// is attempted from both the load handler and the geometry watcher; whichever
// arrives last succeeds.
function onMapLoad(_event: MglEvent<"load">): void {
	mapReady.value = true;
	focusTreatmentArea();
}

watch(
	() => treatment.value?.geometry,
	() => focusTreatmentArea(),
);

const creatorText = computed<string>(() => {
	const t = treatment.value;
	if (!t || t.creator_id === null) return "—";
	return t.creator_name
		? `${t.creator_name} (#${t.creator_id})`
		: `#${t.creator_id}`;
});

const productText = computed<string>(() => {
	const t = treatment.value;
	if (!t || t.product_id === null) return "—";
	return t.product_name
		? `${t.product_name} (#${t.product_id})`
		: `#${t.product_id}`;
});

const areaText = computed<string>(() => {
	const n = treatment.value?.area_treated;
	return n != null ? `${n.toLocaleString()} m²` : "—";
});

const quantityText = computed<string>(() => {
	const t = treatment.value;
	if (!t || t.quantity == null) return "—";
	return t.quantity_unit
		? `${t.quantity.toLocaleString()} ${t.quantity_unit}`
		: t.quantity.toLocaleString();
});

const featureLocationText = computed<string>(() => {
	const loc = feature.value?.location;
	if (loc?.latitude == null || loc?.longitude == null) return "—";
	return `${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}`;
});

const siteAddressText = computed<string>(() => {
	const address = site.value?.address;
	return address ? formatAddress(address) : "—";
});

// Site tags arrive as a plain object over JSON but are typed as a Map, so
// tolerate both (same handling as SiteDetailPanelSingle).
const siteTags = computed<[string, string][]>(() => {
	const tags: unknown = site.value?.tags;
	if (!tags) return [];
	if (tags instanceof Map) return [...tags.entries()];
	if (typeof tags === "object") {
		return Object.entries(tags as Record<string, string>);
	}
	return [];
});
</script>
